// Panel 2 — Podio del turno (mockup "Podio del turno"): top 3 líderes con
// pedestales editoriales (numerales serif gigantes) + "empujando el podio".

import type { PantallaDatos } from 'schemas';
import { colorPct, Corona, ETIQUETA, nombreCorto, PanelVacio, TituloPanel } from '../shared';

export function PanelPodio({ datos }: { datos: PantallaDatos }) {
  const [p1, p2, p3] = datos.lideres;
  const empujando = datos.lideres.slice(3, 6);

  return (
    <section className="flex h-full flex-col gap-4 px-[4vw] py-[3vh]">
      <TituloPanel
        titulo="Podio del turno"
        subtitulo="Top 3 líderes · las posiciones cambian en vivo"
      />
      {!p1 ? (
        <PanelVacio mensaje="El podio se arma con las primeras capturas del turno" />
      ) : (
        <>
          <div className="grid min-h-0 flex-1 grid-cols-3 items-end gap-[3vw] px-[4vw] pb-[1vh]">
            {/* 2º puesto */}
            <Pedestal
              lider={p2 ?? null}
              puesto={2}
              altura="52%"
              pedestalClase="bg-surface-elevated text-fg-tertiary border border-border"
            />
            {/* 1º puesto — pedestal claro, numeral serif gigante (mockup) */}
            <Pedestal
              lider={p1}
              puesto={1}
              altura="78%"
              conCorona
              pedestalClase="text-surface-base"
              pedestalStyle={{ background: 'var(--fg)' }}
            />
            {/* 3º puesto */}
            <Pedestal
              lider={p3 ?? null}
              puesto={3}
              altura="38%"
              pedestalClase="bg-surface-elevated text-fg-tertiary border border-border"
            />
          </div>
          {empujando.length > 0 ? (
            <div className="hidden items-center gap-4 pb-1 lg:flex">
              <span className={`${ETIQUETA} tracking-[0.14em] text-fg-tertiary`}>
                EMPUJANDO EL PODIO
              </span>
              {empujando.map((l, i) => (
                <span
                  key={l.user_id}
                  className="flex items-baseline gap-2 rounded-md border border-border bg-surface-elevated px-3 py-1.5"
                >
                  <span
                    className="text-sm text-fg-tertiary"
                    style={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {i + 4}
                  </span>
                  <span className="text-sm font-medium text-fg">
                    {nombreCorto(l.nombre, l.apellido)}
                  </span>
                  <span
                    className="text-sm font-medium"
                    style={{
                      color: colorPct(l.cumplimiento_pct),
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {l.cumplimiento_pct === null ? '—' : `${Math.round(l.cumplimiento_pct)}%`}
                  </span>
                </span>
              ))}
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}

function Pedestal({
  lider,
  puesto,
  altura,
  conCorona = false,
  pedestalClase,
  pedestalStyle,
}: {
  lider: PantallaDatos['lideres'][number] | null;
  puesto: 1 | 2 | 3;
  altura: string;
  conCorona?: boolean;
  pedestalClase: string;
  pedestalStyle?: React.CSSProperties;
}) {
  return (
    <div className="flex h-full flex-col items-center justify-end gap-4">
      {lider ? (
        <div className="flex flex-col items-center gap-1.5 text-center">
          {conCorona ? (
            <span className="mb-1 inline-flex h-14 w-14 items-center justify-center rounded-full border border-border-strong bg-surface-elevated text-fg">
              <Corona size={26} />
            </span>
          ) : (
            <span
              className="mb-1 inline-flex h-11 w-11 items-center justify-center rounded-full border border-border-strong text-[1.05rem] text-fg-secondary"
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {puesto}
            </span>
          )}
          <p className="font-serif text-[clamp(1.5rem,2.1vw,2.4rem)] leading-tight text-fg">
            {nombreCorto(lider.nombre, lider.apellido)}
          </p>
          <p className="font-mono text-[0.8rem] text-fg-tertiary">
            {[lider.area_nombre, lider.linea_codigo].filter(Boolean).join(' · ') || '—'}
          </p>
          <p
            className="text-[clamp(1.8rem,2.6vw,3rem)] font-medium leading-none"
            style={{ color: colorPct(lider.cumplimiento_pct), fontVariantNumeric: 'tabular-nums' }}
          >
            {lider.cumplimiento_pct === null ? '—' : Math.round(lider.cumplimiento_pct)}
            <span className="text-[0.5em]">%</span>
          </p>
        </div>
      ) : (
        <p className="text-sm text-fg-tertiary">—</p>
      )}
      <div
        className={`flex w-full items-start justify-center rounded-t-lg ${pedestalClase}`}
        style={{ height: altura, ...pedestalStyle }}
      >
        {/* El "1" va sobre el pedestal claro: a 25 % de opacidad no se leía. */}
        <span
          className={`select-none font-serif text-[clamp(4rem,9vw,10rem)] leading-none ${
            puesto === 1 ? 'opacity-90' : 'opacity-25'
          }`}
        >
          {puesto}
        </span>
      </div>
    </div>
  );
}
